import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { useAuth } from '../../../context/AuthContext';
import { CENTRAL_PHARMACY_PATHS } from '../pharmacyPaths';
import { PHARMACY_COPY, statusLabel, CENTRAL_ROLES } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, EmptyBlock, StatusBadge, formatNumber, formatDate } from '../ui';
import SmartSupplyRequestComposer from '../components/SmartSupplyRequestComposer';

const copy = PHARMACY_COPY.REQUESTS;

const STATUS_FILTERS = [
  '',
  'PENDING',
  'APPROVED',
  'PARTIALLY_APPROVED',
  'FULFILLED',
  'REJECTED',
  'CANCELLED'
];

function statusChipLabel(status) {
  if (!status) return copy.FILTER_ALL;
  return copy[`STATUS_${status}`] || statusLabel(status);
}

function priorityClass(priority) {
  return priority === 'URGENT' ? 'cp-status-pill--danger' : 'cp-status-pill--muted';
}

function priorityLabel(priority) {
  return priority === 'URGENT' ? copy.PRIORITY_URGENT : copy.PRIORITY_NORMAL;
}

const emptyCreateForm = { priority: 'NORMAL', notes: '', items: [] };

export default function PharmacyRequestsPage() {
  const navigate = useNavigate();
  const { toast, workSiteId, workSites, centralSite } = usePharmacy();
  const { user } = useAuth();
  const isCentral = CENTRAL_ROLES.includes(user?.role);
  const isOnCentralSite =
    centralSite && workSiteId && String(centralSite.id) === String(workSiteId);
  const canCreate = Boolean(workSiteId) && !isOnCentralSite;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(isCentral ? 'PENDING' : '');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [medications, setMedications] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [detail, setDetail] = useState(null);
  const [approvals, setApprovals] = useState({});
  const [reviewNotes, setReviewNotes] = useState('');
  const [centralStockByMed, setCentralStockByMed] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 220);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter || undefined };
      if (!isCentral && workSiteId) params.site_id = workSiteId;
      const res = await pharmacyService.listSupplyRequests(params);
      setRows(res.success ? res.data || [] : []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, isCentral, workSiteId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    pharmacyService.listMedications({ status: 'ACTIVE' }).then((r) => {
      setMedications(r.success ? r.data || [] : []);
    });
  }, []);

  const siteName = useCallback(
    (id) => workSites.find((s) => String(s.id) === String(id))?.name || `Site #${id}`,
    [workSites]
  );

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    return rows.filter((r) => {
      const hay = `${r.request_number || ''} ${r.site_name || ''}`.toLowerCase();
      return hay.includes(search);
    });
  }, [rows, search]);

  const stats = useMemo(() => {
    const pending = rows.filter((r) => r.status === 'PENDING').length;
    const urgent = rows.filter((r) => r.priority === 'URGENT' && r.status === 'PENDING').length;
    const toDistribute = rows.filter((r) =>
      ['APPROVED', 'PARTIALLY_APPROVED'].includes(r.status)
    ).length;
    return { total: rows.length, pending, urgent, toDistribute };
  }, [rows]);

  const validCreateCount = createForm.items.filter(
    (l) => l.medication_id && Number(l.quantity_requested) > 0
  ).length;

  const openCreate = () => {
    if (!canCreate) {
      toast('warning', copy.CANNOT_CREATE_CENTRAL);
      return;
    }
    setCreateForm(emptyCreateForm);
    setShowCreate(true);
  };

  const submitCreate = async () => {
    if (!validCreateCount) return;
    setBusy(true);
    try {
      const payload = {
        site_id: workSiteId,
        priority: createForm.priority,
        notes: createForm.notes,
        items: createForm.items
          .filter((l) => l.medication_id && Number(l.quantity_requested) > 0)
          .map((l) => ({
            medication_id: Number(l.medication_id),
            quantity_requested: Number(l.quantity_requested)
          }))
      };
      const res = await pharmacyService.createSupplyRequest(payload);
      if (res.success) {
        toast('success', copy.CREATE_OK.replace('{number}', res.data.request_number));
        setShowCreate(false);
        setCreateForm(emptyCreateForm);
        await load();
      } else {
        toast('danger', res.message || copy.FAIL);
      }
    } catch (e) {
      toast('danger', e.message || copy.FAIL);
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (req) => {
    const res = await pharmacyService.getSupplyRequest(req.id);
    if (!res.success) {
      toast('danger', res.message || copy.FAIL);
      return;
    }
    setDetail(res.data);
    const init = {};
    (res.data.items || []).forEach((it) => {
      init[it.id] = it.quantity_approved != null ? it.quantity_approved : it.quantity_requested;
    });
    setApprovals(init);
    setReviewNotes(res.data.review_notes || '');

    if (isCentral && centralSite?.id && res.data.status === 'PENDING') {
      try {
        const stockRes = await pharmacyService.getSiteStock(centralSite.id);
        const map = {};
        (stockRes.success ? stockRes.data || [] : []).forEach((row) => {
          map[String(row.medication_id)] = row;
        });
        setCentralStockByMed(map);
      } catch {
        setCentralStockByMed({});
      }
    } else {
      setCentralStockByMed({});
    }
  };

  const act = async (fn, okMsg, { keepOpen = false } = {}) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.success) {
        toast('success', okMsg);
        if (!keepOpen) setDetail(null);
        else if (res.data) setDetail(res.data);
        await load();
        return res;
      }
      toast('danger', res.message || copy.FAIL);
      return null;
    } catch (e) {
      toast('danger', e.message || copy.FAIL);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const submitReview = (decision) =>
    act(
      () =>
        pharmacyService.reviewSupplyRequest(detail.id, {
          decision,
          review_notes: reviewNotes,
          approvals: Object.entries(approvals).map(([item_id, q]) => ({
            item_id: parseInt(item_id, 10),
            quantity_approved: parseInt(q, 10) || 0
          }))
        }),
      decision === 'REJECTED' ? copy.ACT_REJECT_OK : copy.ACT_APPROVE_OK
    );

  const distribute = async () => {
    const res = await act(
      () => pharmacyService.distributeSupplyRequest(detail.id),
      copy.ACT_DISTRIBUTE_OK
    );
    if (res?.success && res.data?.id && isCentral) {
      navigate(CENTRAL_PHARMACY_PATHS.DISTRIBUTION_DETAIL(res.data.id));
    }
  };

  return (
    <div className="cp-requests-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.TITLE}
        description={isCentral ? copy.DESC_CENTRAL : copy.DESC_SITE}
        actions={
          canCreate ? (
            <button type="button" className="cp-btn cp-btn--primary" onClick={openCreate}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          ) : null
        }
      />

      <section className="cp-catalog-stats" aria-label="Synthèse demandes">
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_TOTAL}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.total)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--amber">
          <span className="cp-catalog-stat__label">{copy.STAT_PENDING}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.pending)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--danger">
          <span className="cp-catalog-stat__label">{copy.STAT_URGENT}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.urgent)}</span>
        </article>
        {isCentral ? (
          <article className="cp-catalog-stat cp-catalog-stat--ok">
            <span className="cp-catalog-stat__label">{copy.STAT_TO_DISTRIBUTE}</span>
            <span className="cp-catalog-stat__value">
              {loading ? '—' : formatNumber(stats.toDistribute)}
            </span>
          </article>
        ) : null}
      </section>

      <div className="cp-card">
        <div className="cp-catalog-toolbar">
          <div className="cp-search-wrap">
            <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
            <input
              type="search"
              className="cp-search-wrap__input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_ARIA}
            />
          </div>
          <div className="cp-filter-pills" role="group" aria-label={copy.FILTER_STATUS_ARIA}>
            {STATUS_FILTERS.map((s) => (
              <button
                key={s || 'all'}
                type="button"
                className={`cp-filter-pill${statusFilter === s ? ' is-active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {statusChipLabel(s)}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="cp-icon-btn"
            onClick={load}
            disabled={loading}
            aria-label={copy.REFRESH_ARIA}
            title={copy.REFRESH_ARIA}
          >
            <Icon name="refresh" size={16} />
          </button>
        </div>

        <div className="cp-card__body cp-card__body--flush">
          {loading ? (
            <LoadingBlock />
          ) : filteredRows.length === 0 ? (
            <EmptyBlock label={copy.EMPTY} />
          ) : (
            <div className="cp-table-wrap">
              <Table responsive hover className="cp-table mb-0">
                <thead>
                  <tr>
                    <th>{copy.COL_NUMBER}</th>
                    <th>{copy.COL_SITE}</th>
                    <th>{copy.COL_PRIORITY}</th>
                    <th className="text-end">{copy.COL_LINES}</th>
                    <th>{copy.COL_STATUS}</th>
                    <th>{copy.COL_DATE}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.id} className="cp-table-row--clickable" onClick={() => openDetail(r)}>
                      <td>
                        <strong>{r.request_number}</strong>
                      </td>
                      <td>{r.site_name}</td>
                      <td>
                        <span className={`cp-status-pill ${priorityClass(r.priority)}`}>
                          {priorityLabel(r.priority)}
                        </span>
                      </td>
                      <td className="text-end cp-num">{formatNumber(r.items_count)}</td>
                      <td>
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="cp-num text-muted">{formatDate(r.created_at)}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="cp-btn cp-btn--ghost cp-btn--sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(r);
                          }}
                        >
                          {copy.OPEN}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={showCreate}
        onHide={() => !busy && setShowCreate(false)}
        size="xl"
        centered
        scrollable
        className="cp-modal"
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <Modal.Title as="h2" className="cp-modal__title">
            {copy.CREATE_TITLE}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          <SmartSupplyRequestComposer
            medications={medications}
            siteId={workSiteId}
            siteName={siteName(workSiteId)}
            value={createForm}
            onChange={setCreateForm}
            copy={copy}
          />
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button
            type="button"
            className="cp-btn cp-btn--ghost"
            onClick={() => setShowCreate(false)}
            disabled={busy}
          >
            {copy.CREATE_CANCEL}
          </button>
          <button
            type="button"
            className="cp-btn cp-btn--primary"
            onClick={submitCreate}
            disabled={busy || !validCreateCount}
          >
            {copy.CREATE_SUBMIT}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!detail}
        onHide={() => !busy && setDetail(null)}
        size="lg"
        centered
        scrollable
        className="cp-modal"
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <Modal.Title as="h2" className="cp-modal__title">
            {detail?.request_number}{' '}
            {detail ? <StatusBadge status={detail.status} /> : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          {detail ? (
            <>
              <div className="cp-reports-med-detail__stats mb-3">
                <div>
                  <span className="cp-reports-med-detail__label">{copy.DETAIL_SITE}</span>
                  <strong>{detail.site_name}</strong>
                </div>
                <div>
                  <span className="cp-reports-med-detail__label">{copy.DETAIL_PRIORITY}</span>
                  <strong>{priorityLabel(detail.priority)}</strong>
                </div>
                <div>
                  <span className="cp-reports-med-detail__label">{copy.COL_DATE}</span>
                  <strong>{formatDate(detail.created_at)}</strong>
                </div>
              </div>

              {detail.notes ? (
                <p className="small text-muted mb-2">
                  <strong>{copy.DETAIL_NOTES} :</strong> {detail.notes}
                </p>
              ) : null}

              <div className="cp-table-wrap">
                <Table size="sm" className="cp-table mb-0">
                  <thead>
                    <tr>
                      <th>{copy.COL_MED}</th>
                      <th className="text-end">{copy.COL_REQUESTED}</th>
                      <th className="text-end">{copy.COL_APPROVED}</th>
                      <th className="text-end">{copy.COL_DELIVERED}</th>
                      {isCentral && detail.status === 'PENDING' ? (
                        <>
                          <th className="text-end">{copy.COL_CENTRAL_STOCK}</th>
                          <th className="text-end">{copy.COL_TO_APPROVE}</th>
                        </>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it) => {
                      const central = centralStockByMed[String(it.medication_id)];
                      return (
                        <tr key={it.id}>
                          <td>
                            <strong>{it.medication_name}</strong>
                            {it.unit_name ? (
                              <span className="text-muted small ms-1">{it.unit_name}</span>
                            ) : null}
                          </td>
                          <td className="text-end cp-num">{formatNumber(it.quantity_requested)}</td>
                          <td className="text-end cp-num">
                            {it.quantity_approved != null ? formatNumber(it.quantity_approved) : '—'}
                          </td>
                          <td className="text-end cp-num">{formatNumber(it.quantity_delivered)}</td>
                          {isCentral && detail.status === 'PENDING' ? (
                            <>
                              <td className="text-end cp-num">
                                {central ? formatNumber(central.on_hand) : '—'}
                              </td>
                              <td style={{ maxWidth: 110 }}>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min={0}
                                  max={it.quantity_requested}
                                  value={approvals[it.id] ?? ''}
                                  onChange={(e) =>
                                    setApprovals((p) => ({ ...p, [it.id]: e.target.value }))
                                  }
                                />
                              </td>
                            </>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {isCentral && detail.status === 'PENDING' ? (
                <Form.Group className="mt-3">
                  <Form.Label>{copy.FIELD_REVIEW_NOTES}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                  />
                </Form.Group>
              ) : null}

              {detail.review_notes && detail.status !== 'PENDING' ? (
                <p className="small text-muted mt-3 mb-0">
                  <strong>{copy.DETAIL_REVIEW} :</strong> {detail.review_notes}
                </p>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          {detail && isCentral && detail.status === 'PENDING' ? (
            <>
              <button
                type="button"
                className="cp-btn cp-btn--danger"
                disabled={busy}
                onClick={() => submitReview('REJECTED')}
              >
                {copy.ACT_REJECT}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                disabled={busy}
                onClick={() => submitReview('APPROVE')}
              >
                {copy.ACT_APPROVE}
              </button>
            </>
          ) : null}
          {detail &&
          isCentral &&
          ['APPROVED', 'PARTIALLY_APPROVED'].includes(detail.status) ? (
            <button type="button" className="cp-btn cp-btn--primary" disabled={busy} onClick={distribute}>
              {copy.ACT_DISTRIBUTE}
            </button>
          ) : null}
          {detail &&
          (detail.status === 'PENDING' ||
            (isCentral && ['APPROVED', 'PARTIALLY_APPROVED'].includes(detail.status))) ? (
            <button
              type="button"
              className="cp-btn cp-btn--ghost"
              disabled={busy}
              onClick={() =>
                act(() => pharmacyService.cancelSupplyRequest(detail.id), copy.ACT_CANCEL_OK)
              }
            >
              {copy.ACT_CANCEL}
            </button>
          ) : null}
          <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setDetail(null)}>
            {copy.CLOSE}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
